import { Box } from "@mui/material";
import { Datagrid, FunctionField, TextField, useGetList, useRecordContext, useTranslate } from "react-admin";

import { LocationRecord } from "../types/location";
import { SourceSystemChip } from "./fields/SourceSystemChip";

const NestedLocationDataGrid = ({
  children,
  allLocations,
  depth,
}: {
  children: LocationRecord[];
  allLocations: LocationRecord[];
  depth: number;
}) => {
  const translate = useTranslate();
  const indentStep = 2;

  const isRowExpandable = (record: LocationRecord) => {
    if (!allLocations || !record?.id) return false;
    return allLocations.some(loc => loc.partOfId === record.id);
  };

  return (
    <Box>
      <Datagrid
        data={children}
        rowClick="show"
        bulkActionButtons={false}
        expand={<LocationChildrenExpand depth={depth + 1} />}
        isRowExpandable={isRowExpandable}
        sx={{
          "& .RaDatagrid-tableWrapper": {
            boxShadow: "none",
          },
          "& .RaDatagrid-thead": {
            display: "none",
          },
          "& .MuiTableBody-root .MuiTableCell-root:first-of-type": {
            width: 48,
            minWidth: 48,
            paddingRight: 0,
            overflow: "visible",
            boxSizing: "border-box",
          },
          "& .MuiTableBody-root .MuiTableCell-root:first-of-type .MuiIconButton-root": {
            marginLeft: depth * indentStep,
          },
          "& .MuiTable-root": {
            tableLayout: "fixed",
          },
          "& .MuiTableBody-root .MuiTableCell-body[colspan]": {
            padding: 0,
          },
        }}
      >
        <TextField source="name" sortable={false} label="Name" sx={{ width: "25%", marginLeft: depth * indentStep }} />
        <FunctionField
          label="Quelle"
          sortable={false}
          sx={{ width: "10%" }}
          render={record => <SourceSystemChip record={record} translatePrefix="Location.fields.sourceSystem" />}
        />
        <FunctionField
          source="type"
          sortable={false}
          label="Typ"
          sx={{ width: "14%" }}
          render={record => {
            if (!record.type) return "";
            return translate(`locationType.${record.type}`);
          }}
        />
        <TextField source="patientCount" sortable={false} label="Patient.countLabel" sx={{ width: "12%" }} />
        <TextField source="practitionerCount" sortable={false} label="Mitarbeiter" sx={{ width: "12%" }} />
        <TextField source="deviceCount" sortable={false} label="Geräte" sx={{ width: "12%" }} />
        <TextField source="externalId" sortable={false} label="External ID" sx={{ width: "15%" }} />
      </Datagrid>
    </Box>
  );
};

export const LocationChildrenExpand = ({ depth = 1 }: { depth?: number }) => {
  const record = useRecordContext<LocationRecord>();
  const { data: allLocations } = useGetList("Location", {
    filter: { organizationId: record?.organizationId },
    pagination: { page: 1, perPage: 1000 },
  });

  if (!record?.id || !allLocations) return null;

  // Find direct children of this location
  const children = allLocations.filter(loc => loc.partOfId === record.id);

  return <NestedLocationDataGrid children={children} allLocations={allLocations} depth={depth} />;
};
